import axios from 'axios';

// Conversation turns (Gemini `contents` format). The server returns the
// canonical history via the `messages` event; the client persists and replays it.
export interface AssistantMessage {
  role: string;
  content?: unknown;
  parts?: unknown;
}

export interface ClientContext {
  route?: string;
  team_id?: number;
  match_id?: number;
  view?: string;
  visible_summary?: string;
}

/** A Gemini inline-image content part. `data` is base64; the backend decodes it
 *  to real bytes only at the moment of the model call. */
export interface InlineImagePart {
  inline_data: { mime_type: string; data: string };
}

/** Split a `data:<mime>;base64,<payload>` URL (as produced by FileReader) into
 *  its mime type and base64 payload. Returns null for anything that isn't a
 *  base64 data URL. Exported for unit tests. */
export function parseDataUrl(dataUrl: string): { mimeType: string; base64: string } | null {
  const match = /^data:([^;,]+)(?:;[^;,]+)*;base64,(.+)$/s.exec(dataUrl);
  if (!match) return null;
  return { mimeType: match[1], base64: match[2] };
}

/** Build a Gemini inline-image part from a mime type + base64 payload. */
export function buildImagePart(mimeType: string, base64: string): InlineImagePart {
  return { inline_data: { mime_type: mimeType, data: base64 } };
}

export interface ToolAction {
  tool_name: string;
  args: Record<string, unknown>;
  summary: string;
}

// A batch of write actions the assistant wants to apply, awaiting confirmation.
export interface PendingAction {
  actions: ToolAction[];
}

export interface Approval {
  decision: 'approved' | 'rejected';
}

/** Per-user monthly AI spend snapshot. Sent by the server in a `usage` SSE event
 *  on every chat turn, or returned by GET /assistant/usage. */
export interface AssistantUsage {
  spent_usd: number;
  limit_usd: number;
  remaining_usd: number;
  percent_used: number;
  is_blocked: boolean;
  is_warning: boolean;
  admin_email?: string;
}

/** Thrown when the server refuses a turn because the user is over their monthly cap. */
export interface AssistantLimitReached extends AssistantUsage {
  error: 'ai_limit_reached';
  message: string;
  admin_email: string;
}

/** Reason a pre-stream attempt is being retried. Surfaced to the UI so it can
 *  show "Retrying…" instead of a hard error during the auto-retry window. */
export type AssistantRetryReason = 'network' | 'server_5xx' | 'rate_limit';

export interface AssistantStreamHandlers {
  onText?: (text: string) => void;
  onPendingAction?: (action: PendingAction) => void;
  onMessages?: (messages: AssistantMessage[]) => void;
  onUsage?: (usage: AssistantUsage) => void;
  onLimitReached?: (info: AssistantLimitReached) => void;
  onError?: (detail: string) => void;
  onRetry?: (attempt: number, reason: AssistantRetryReason) => void;
  onDone?: () => void;
}

export interface SendAssistantMessageParams {
  messages: AssistantMessage[];
  clientContext?: ClientContext | null;
  approval?: Approval | null;
  // Stable per-conversation id; sent as PostHog's $ai_session_id so all turns
  // of one chat group into a single AI-observability session.
  sessionId?: string;
  signal?: AbortSignal;
}

/** Pre-stream retry tuning. Auto-retry only fires before any tokens have been
 *  streamed back, so the LLM call hasn't been billed yet and replaying is safe. */
const RETRY_MAX_ATTEMPTS = 3;
const RETRY_BASE_MS = 400;
const RETRY_CEILING_MS = 8000;
const RETRY_RATE_LIMIT_FLOOR_MS = 2000;

/** Classify a pre-stream failure. Returns null for non-retryable cases (auth,
 *  not-found, validation, monthly limit). Exported for unit tests. */
export function classifyPreStream(status: number | 'network'): AssistantRetryReason | null {
  if (status === 'network') return 'network';
  if (status === 429) return 'rate_limit';
  if (status >= 500 && status < 600) return 'server_5xx';
  return null;
}

/** Exported for unit tests. */
export function delayFor(
  reason: AssistantRetryReason,
  attempt: number,
  retryAfterMs: number | null
): number {
  if (reason === 'rate_limit') {
    const base = Math.max(retryAfterMs ?? 0, RETRY_RATE_LIMIT_FLOOR_MS);
    return Math.min(base, RETRY_CEILING_MS);
  }
  const exp = Math.min(RETRY_BASE_MS * 2 ** (attempt - 1), 4000);
  const jitter = Math.floor(Math.random() * 200);
  return Math.min(exp + jitter, RETRY_CEILING_MS);
}

/** Sleep that resolves immediately if the AbortSignal fires, so a caller-side
 *  abort doesn't have to wait out the full backoff before bailing. */
function abortableSleep(ms: number, signal?: AbortSignal): Promise<void> {
  return new Promise((resolve) => {
    if (signal?.aborted) {
      resolve();
      return;
    }
    const timer = setTimeout(() => {
      signal?.removeEventListener('abort', onAbort);
      resolve();
    }, ms);
    const onAbort = () => {
      clearTimeout(timer);
      resolve();
    };
    signal?.addEventListener('abort', onAbort, { once: true });
  });
}

/** Parse a `Retry-After` HTTP header (seconds or HTTP-date) into milliseconds.
 *  Exported for unit tests. */
export function parseRetryAfter(header: string | null): number | null {
  if (!header) return null;
  const seconds = Number(header);
  if (Number.isFinite(seconds) && seconds >= 0) return seconds * 1000;
  const when = Date.parse(header);
  if (!Number.isNaN(when)) return Math.max(0, when - Date.now());
  return null;
}

function dispatchEvent(rawEvent: string, handlers: AssistantStreamHandlers): void {
  let event = 'message';
  const dataLines: string[] = [];
  for (const line of rawEvent.split('\n')) {
    if (line.startsWith('event:')) {
      event = line.slice('event:'.length).trim();
    } else if (line.startsWith('data:')) {
      dataLines.push(line.slice('data:'.length).trim());
    }
  }
  if (dataLines.length === 0) return;

  let data: any;
  try {
    data = JSON.parse(dataLines.join('\n'));
  } catch {
    return;
  }

  switch (event) {
    case 'text':
      handlers.onText?.(data.text ?? '');
      break;
    case 'pending_action':
      handlers.onPendingAction?.(data as PendingAction);
      break;
    case 'messages':
      handlers.onMessages?.(data.messages ?? []);
      break;
    case 'usage':
      handlers.onUsage?.(data as AssistantUsage);
      break;
    case 'error':
      handlers.onError?.(data.detail ?? 'Assistant error');
      break;
    case 'done':
      handlers.onDone?.();
      break;
    default:
      break;
  }
}

/**
 * Stream a chat turn from the captain assistant. Uses fetch (not EventSource)
 * because the endpoint is a POST with a JSON body and a Bearer token.
 */
export async function sendAssistantMessage(
  params: SendAssistantMessageParams,
  handlers: AssistantStreamHandlers
): Promise<void> {
  const token = localStorage.getItem('token');
  const baseURL = axios.defaults.baseURL ?? '';

  // Pre-stream retry loop. Auto-retries transient network/5xx/429 failures
  // before any tokens have streamed back. 402/404/other 4xx short-circuit.
  let response: Response | null = null;
  for (let attempt = 1; attempt <= RETRY_MAX_ATTEMPTS; attempt += 1) {
    if (params.signal?.aborted) return;

    let fetchError = false;
    try {
      response = await fetch(`${baseURL}/assistant/chat`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({
          messages: params.messages,
          client_context: params.clientContext ?? null,
          approval: params.approval ?? null,
          session_id: params.sessionId ?? null,
        }),
        signal: params.signal,
      });
    } catch {
      fetchError = true;
    }

    // 402 is terminal — surface the structured limit-reached payload and stop.
    if (response && response.status === 402) {
      try {
        const body = await response.json();
        const detail = body?.detail ?? body;
        if (detail && detail.error === 'ai_limit_reached') {
          handlers.onLimitReached?.(detail as AssistantLimitReached);
          return;
        }
      } catch {
        /* fall through to generic error below */
      }
    }

    const reason = fetchError
      ? classifyPreStream('network')
      : response && !response.ok
        ? classifyPreStream(response.status)
        : null;

    if (reason && attempt < RETRY_MAX_ATTEMPTS) {
      const retryAfter = response?.headers ? parseRetryAfter(response.headers.get('Retry-After')) : null;
      handlers.onRetry?.(attempt + 1, reason);
      await abortableSleep(delayFor(reason, attempt, retryAfter), params.signal);
      response = null;
      continue;
    }

    if (fetchError) {
      handlers.onError?.('Could not reach the assistant.');
      return;
    }
    break;
  }

  if (!response) return;

  if (!response.ok || !response.body) {
    handlers.onError?.(
      response.status === 404
        ? 'The assistant is not available.'
        : `The assistant request failed (${response.status}).`
    );
    return;
  }

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';

  try {
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });

      let boundary = buffer.indexOf('\n\n');
      while (boundary !== -1) {
        const rawEvent = buffer.slice(0, boundary);
        buffer = buffer.slice(boundary + 2);
        dispatchEvent(rawEvent, handlers);
        boundary = buffer.indexOf('\n\n');
      }
    }
  } catch {
    // Mid-stream network drop — never auto-retry (cost may already be incurred
    // server-side). Surface for manual retry.
    handlers.onError?.('The assistant connection dropped. Tap retry to try again.');
    return;
  }

  if (buffer.trim()) {
    dispatchEvent(buffer, handlers);
  }
}

/** Snapshot the current user's monthly AI spend without sending a chat turn. */
export async function fetchAssistantUsage(): Promise<AssistantUsage | null> {
  try {
    const response = await axios.get<AssistantUsage>('/assistant/usage');
    return response.data;
  } catch {
    return null;
  }
}
